import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
// --------------------------------------------------------------------------------
import { String, StringProps } from '../String';
import { DropDown, DropDownProps, DropDownBlobProps } from '../DropDown';
import { config, Icons } from '../../index';

export interface SelectDropDownProps {
  /* Label */
  label?: StringProps;
  /* Dropdown title*/
  title?: TitleProps[];
  dropdownConfig?: DropDownProps;
  /* Placeholder title */
  placeholder?: string;
  /* Input name */
  name?: string; // input element will be ignored if name pop not provided
  /* Input default value */
  value?: string;
  /* Input width */
  width?: string;
  /* Input min height */
  minHeight?: string;
  /* Input max height */
  maxHeight?: string;
  /* Input height */
  height?: string;
  /* Input margin */
  margin?: string;
  /* Input padding */
  padding?: string;
  /* Input font size */
  fontSize?: string;
  /* Input border radius */
  borderRadius?: string;
  /* Input background color */
  backgroundColor?: string;
  /* Input border */
  border?: string;
  /* Extend input with additional classes */
  className?: string;
  /* Drop down state primary */
  isActive?: boolean;
  // --------------------------------------------------------------------------------
  /* Input onClick handler */
  onClick?: (event?: React.MouseEvent) => void;
  /* Input onChange handler */
  onChange?: (event?: OnChangeProps | any) => void;
}

export interface TitleProps {
  title: StringProps;
  value?: string;
  onClick: (event?: React.MouseEvent) => void;
}
export interface OnChangeProps {
  target?: TargetProps;
}

export interface TargetProps {
  name?: string;
  value?: string | any;
  type?: string;
  url?: string;
}

const StyledWrapper = styled.div<SelectDropDownProps>`
  position: relative;
`;

const ActionIconWrapper = styled.div<SelectDropDownProps>`
  transition: ${config.transitionShort};
  transform: ${(props) =>
    props.isActive ? `rotate(180deg)` : `rotate(45deg)`};
  transform-origin: center;
`;

const StyledDropDownHeader = styled.div<SelectDropDownProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${(props) => props.height};
  padding: ${config.spacingXSmall} ${config.spacingSmall};
  border-radius: ${(props) => props.borderRadius};
  background-color: ${(props) => props.backgroundColor};
  border: ${(props) => props.border};
  cursor: pointer;
  user-select: none;
  transition: ${config.transitionMedium};

  &:hover {
    opacity: 0.8;
  }
`;

// 📌 Primary UI component for user interaction
export const SelectDropDown: React.FC<SelectDropDownProps> = ({
  label,
  title,
  placeholder,
  dropdownConfig,
  // --------------------------------------------------------------------------------
  name,
  value,
  width = 'fit-content',
  minHeight = 'unset',
  maxHeight = 'unset',
  border = `1px solid ${config.muted}`,
  borderRadius = config.spacingSmall,
  fontSize = config.fontSmall,
  margin = 'unset',
  padding = 'unset',
  height,
  backgroundColor = config.meadow,
  // --------------------------------------------------------------------------------
  onClick,
  onChange,
  // additional props
  ...props
}) => {
  const [isActive, setActive] = useState(false);
  const [selectedOption, setSelectedOption] =
    useState<DropDownBlobProps | undefined>();

  // --------------------------------------------------------------------------------
  const toggleOnClick = () => {
    // additional onClick handler
    onClick?.();
    setActive(!isActive);
  };

  useEffect(() => {
    // find selected option
    if (value) {
      const selected = title?.find((item) => item.value === value);
      if (selected) {
        // set dropdown initial value if provided
        setSelectedOption(selected);
      }
    }
  }, [value, title]);

  useEffect(() => {
    // --------------------------------------------------------------------------------
    // 📌  Pass onChange event to parent component
    // --------------------------------------------------------------------------------

    // pass onChange as event handler
    if (selectedOption)
      onChange?.({
        target: {
          value: selectedOption?.value || selectedOption?.title || '',
          name: name || selectedOption?.title || '',
          type: 'change', // type is required for onChange
        },
        isTrusted: true,
      });

    // 👇️ disable the rule for a single line
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  return (
    <StyledWrapper {...props}>
      <StyledDropDownHeader
        backgroundColor={backgroundColor}
        border={border}
        borderRadius={borderRadius}
        onClick={toggleOnClick}
        name={name}
        height={height}
      >
        <String
          fontSize={fontSize}
          fontWeight="400"
          color={selectedOption ? config.darkMuted : config.muted}
          limit={1}
          cursor="pointer"
          string={
            selectedOption?.title?.string || placeholder || 'Please select' // placeholder for user interaction
          }
        />
        <ActionIconWrapper isActive={isActive}>
          <Icons
            icon="close"
            fill={label?.color}
            hoverFill={label?.color}
            size={12}
            onClick={() => {}} // default icon behavior for onClick
          />
        </ActionIconWrapper>
      </StyledDropDownHeader>

      <DropDown
        dropDownProps={title}
        isActive={isActive}
        {...dropdownConfig}
        // add call back to close dropdown
        onClick={(props) => {
          // set selected option
          setSelectedOption(props);
          // close dropdown if selected
          setActive(!isActive);
        }}
      />
    </StyledWrapper>
  );
};
